import Layout from '@components/layout'
import { blogAuthorPageModuleQuery } from '@data/sanity/queries/page'
import { SanityBlogCategory } from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getBlogCanonicalUrl } from '@lib/blog'
import { Locale, getAllLocales } from '@lib/language'
import {
  BasePageProps,
  getNonNullValues,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext, StaticPathWithSlug } from '@lib/routes'
import {
  getAllBlogSettings,
  getBlogAuthorPage,
  getBlogLocale,
} from '@lib/sanity/blog'
import { getSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAllSlugs } from '@lib/sanity/page'
import { getVideoObjectSchemas } from '@lib/schema/video'
import { getShopifyShopForPageWithCache } from '@lib/shopify/store-cache'
import Modules from '@modules/modules'
import { GetStaticPathsResult, GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { ParsedUrlQuery } from 'querystring'
import { useMemo } from 'react'

const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type BlogAuthorPageProps = BasePageProps & {
  blogLocale: Locale
  slug: string
  page: SanityBlogCategory
  site: SanitySiteFragment
}

const BlogAuthorPage = ({
  draftMode,
  locale,
  blogLocale,
  slug,
  page,
  site,
}: BlogAuthorPageProps) => {
  const canonicalUrl = getBlogCanonicalUrl(
    'blogAuthor',
    slug,
    site.blogSettings,
  )

  const schemas = useMemo(() => {
    const videoObjectSchemas = getVideoObjectSchemas(page)
    return [...videoObjectSchemas]
  }, [page])

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      schemas={schemas}
      draftMode={draftMode}
    >
      {!draftMode && <Modules modules={page.modules} />}
      {draftMode && (
        <PreviewModules
          modules={page.modules}
          query={blogAuthorPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
            blogAuthorSlug: slug,
          }}
        />
      )}
    </Layout>
  )
}

interface BlogAuthorPageParams extends ParsedUrlQuery {
  slug: string
}

export async function getStaticProps(
  context: CustomStaticPropsContext<BlogAuthorPageParams>,
): Promise<GetStaticPropsResult<BlogAuthorPageProps>> {
  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const slug = context.params?.slug ?? ''
  const blogLocale = await getBlogLocale(sanityClient, context.locale)
  const blogAuthorPage = await getBlogAuthorPage(
    sanityClient,
    context.locale,
    blogLocale,
    slug,
  )

  if (!blogAuthorPage.page) {
    return {
      notFound: true,
      revalidate: 600,
    }
  }

  // Shopify data
  const shopifyShop = await getShopifyShopForPageWithCache(context.locale)

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      slug,
      page: getNonNullValues(blogAuthorPage.page),
      site: getNonNullValues(blogAuthorPage.site),
      shopifyShop,
    },
    revalidate: 600,
  }
}

export async function getStaticPaths(): Promise<
  GetStaticPathsResult<BlogAuthorPageParams>
> {
  if (process.env.NEXT_PUBLIC_APP_ENV === 'preview') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const sanityClient = getSanityClient()
  const blogAuthors = await getAllSlugs(sanityClient, 'blogAuthor')
  const siteLocales = getAllLocales()
  const blogSettings = await getAllBlogSettings(sanityClient, siteLocales)
  const paths: StaticPathWithSlug[] = []

  blogAuthors.forEach((blogAuthor) => {
    const locale = blogAuthor.locale as Locale

    if (blogSettings.hasLocaleContent[locale]) {
      // Add page locale path
      paths.push({
        params: {
          slug: blogAuthor.slug.current,
        },
        locale,
      })

      // Add inherited locale paths
      for (const childLocale of blogSettings.childLocales[locale]) {
        paths.push({
          params: {
            slug: blogAuthor.slug.current,
          },
          locale: childLocale,
        })
      }
    }
  })

  return {
    paths,
    fallback: 'blocking',
  }
}

export default BlogAuthorPage
